/**
 * @description Initializes the personnel form based on the "access required" options and personnel list passed in
 */

import React from 'react';
import cloneDeep from 'lodash/cloneDeep';

const useInitializePersonnelForm = (form, personnel) => {
  // This useLayoutEffect resets the form field values based on changes to the access required options and personnel list
  React.useLayoutEffect(() => {
    // Do nothing if there is no form data
    if (!personnel) {
      return;
    }

    // Retrieve the current form field values
    const {
      personnel: currPersonnel = [],
      hiddenSelectedPersonnel: currHiddenSelectedPersonnel = {},
      selectedPersonnel: currSelectedPersonnel = {}
    } = form.getValues();

    // Convert the personnel list into a map for quick lookup
    const currPersonnelMap = currPersonnel.reduce((obj, entry) => {
      // eslint-disable-next-line no-param-reassign
      obj[entry.personnelId] = entry;

      return obj;
    }, {});

    // Declare new form field values
    const newPersonnel = cloneDeep(personnel);
    const newHiddenSelectedPersonnel = cloneDeep(currHiddenSelectedPersonnel);
    const newSelectedPersonnel = {};

    // Iterate through the list of currently selected personnel and add them to the hidden selected personnel list
    //   We're going to first assume that all selected personnel will be hidden, and then pop them off the list as we
    //   discover otherwise in the next segment
    for (const id of Object.keys(currSelectedPersonnel)) {
      const person = currPersonnelMap[id];

      if (person) {
        newHiddenSelectedPersonnel[id] = { ...cloneDeep(person) };
      }
    }

    // Iterate through the list of new personnel and, if the new hidden selected personnel field has that personnel...
    //   (1) Clone the settings of the new hidden selected personnel record into the new personnel record
    //   (2) Remove this personnel from the new hidden selected personnel (since it won't be hidden)
    //   (3) Add this personnnel to the selected personnel field (since it will be visible)
    for (let index = 0; index < newPersonnel.length; index++) {
      const person = newPersonnel[index];

      if (newHiddenSelectedPersonnel[person.personnelId]) {
        newPersonnel[index] = cloneDeep(newHiddenSelectedPersonnel[person.personnelId]);
        delete newHiddenSelectedPersonnel[person.personnelId];
        newSelectedPersonnel[person.personnelId] = true;
      }
    }

    // Reset the form with the new personnel list, hidden selected personnel list, and selected personnel list
    form.reset({
      personnel: newPersonnel,
      hiddenSelectedPersonnel: newHiddenSelectedPersonnel,
      selectedPersonnel: newSelectedPersonnel
    }, { keepDirty: true, keepErrors: true });
  }, [personnel]);
};

export default useInitializePersonnelForm;
